/**
 * Programmatic OHLC prefetch for agent / DSL (off-chart symbols).
 */

import type { OHLCBatchResponse, OHLCRequest } from "@/vybe-trader-client/chart/types";
import type { Symbol } from "@/vybe-trader-client/symbols/types";
import type { OHLCData } from "@/vybe-trader-client/chart/types";
import {
  parseMarketDataDate,
  parseMarketDataRangeEnd,
  parseMarketDataRangeStart,
} from "@/lib/terminal/engine/shared/market-data";
import { buildMarketDataRangeSummary } from "@/lib/agent/market-data-range-summary";
import { marketDataDb } from "@/lib/market-data-db";
import {
  barsRangeFromSeries,
  loadIdbBarsForSymbol,
} from "@/lib/market-data-db/idb-read";

const prefetchTargets = new Set<string>();
const inflightOhLcRequestKeys = new Set<string>();

const CHUNK_MS = 90 * 24 * 60 * 60 * 1000;
const DEFAULT_TIMEOUT_MS = 30_000;
const POLL_MS = 400;
/** Keep accepting off-chart OHLC batches after the wait loop ends. */
const PREFETCH_TARGET_LINGER_MS = 60_000;

function ohlcRequestKey(symbol: string, from: Date, to: Date): string {
  return `${symbol}:${from.getTime()}:${to.getTime()}`;
}

export function isOhlcPrefetchTarget(currency: string): boolean {
  return prefetchTargets.has(currency.toUpperCase());
}

export function registerOhlcPrefetchTarget(currency: string): void {
  prefetchTargets.add(currency.toUpperCase());
}

export function unregisterOhlcPrefetchTarget(currency: string): void {
  prefetchTargets.delete(currency.toUpperCase());
}

export function ohlcBarsFromBatch(batch: OHLCBatchResponse): OHLCData[] {
  if (!batch.success) return [];
  return batch.bars.map((bar) => ({
    timestamp: new Date(bar.time).getTime(),
    open: bar.openPrice,
    high: bar.highPrice,
    low: bar.lowPrice,
    close: bar.closePrice,
    volume: bar.tickVolume,
  }));
}

async function resolveActiveAccountKey(): Promise<string> {
  const { useAccountsStore } = await import("@/lib/accounts/store");
  const { activeAccountId, accounts } = useAccountsStore.getState();
  if (!activeAccountId) throw new Error("No active trading account");
  const account = accounts.find((a) => a.id === activeAccountId);
  if (!account?.companyName || !account?.serverName) {
    throw new Error("Active account is missing company or server name");
  }
  return `${account.companyName}_${account.serverName}`;
}

async function sendOhlcRangeRequest(
  symbol: Symbol,
  from: Date,
  to: Date,
): Promise<void> {
  const { VybeTraderClient } = await import(
    "@/vybe-trader-client/connection/client/VybeTraderClient"
  );
  const client = VybeTraderClient.getInstance();
  if (!client.isConnected()) {
    throw new Error("Not connected to trading server");
  }

  const key = ohlcRequestKey(symbol.currency, from, to);
  if (inflightOhLcRequestKeys.has(key)) return;
  inflightOhLcRequestKeys.add(key);

  const request: OHLCRequest = {
    symbol: symbol.currency,
    period: 0,
    customFrom: from.toISOString(),
    customTo: to.toISOString(),
  };

  try {
    await client.send("GetOHLCData", request);
  } finally {
    inflightOhLcRequestKeys.delete(key);
  }
}

function dateChunks(fromTs: number, toTs: number): Array<{ from: Date; to: Date }> {
  const chunks: Array<{ from: Date; to: Date }> = [];
  let cursor = fromTs;
  while (cursor <= toTs) {
    const end = Math.min(cursor + CHUNK_MS - 1, toTs);
    chunks.push({ from: new Date(cursor), to: new Date(end) });
    cursor = end + 1;
  }
  return chunks;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function schedulePrefetchTargetUnregister(currency: string): void {
  setTimeout(() => unregisterOhlcPrefetchTarget(currency), PREFETCH_TARGET_LINGER_MS);
}

async function getCachedWindowRange(
  accountKey: string,
  symbol: string,
  fromTs: number,
  toTs: number,
): Promise<{ minTs: number; maxTs: number; count: number } | null> {
  await marketDataDb.invalidateSymbolCache(accountKey, symbol);
  const bars = await loadIdbBarsForSymbol(accountKey, symbol, { fromTs, toTs });
  return barsRangeFromSeries(bars);
}

function rangeCoversRequest(
  range: { minTs: number; maxTs: number },
  fromTs: number,
  toTs: number,
): boolean {
  const slack = 86_400_000;
  return range.minTs <= fromTs + slack && range.maxTs >= toTs - slack;
}

export interface PrefetchOhlcResult extends Record<string, unknown> {
  symbol: string;
  requestedFrom: string;
  requestedTo: string;
  loaded: boolean;
  hubConnected: boolean;
  requestsSent: number;
}

export async function prefetchOhlcRange(
  symbol: Symbol,
  fromDate: string,
  toDate: string,
  opts?: { timeoutMs?: number },
): Promise<PrefetchOhlcResult> {
  const currency = symbol.currency.toUpperCase();
  const fromTs = parseMarketDataRangeStart(parseMarketDataDate(fromDate));
  const toTs = parseMarketDataRangeEnd(parseMarketDataDate(toDate));
  if (fromTs > toTs) throw new Error("fromDate must be on or before toDate");

  const accountKey = await resolveActiveAccountKey();
  const { VybeTraderClient } = await import(
    "@/vybe-trader-client/connection/client/VybeTraderClient"
  );
  const hubConnected = VybeTraderClient.getInstance().isConnected();
  if (!hubConnected) {
    throw new Error("Not connected to trading server — connect an account first");
  }

  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  registerOhlcPrefetchTarget(currency);
  try {
    const chunks = dateChunks(fromTs, toTs);
    let requestsSent = 0;
    for (const chunk of chunks) {
      await sendOhlcRangeRequest(symbol, chunk.from, chunk.to);
      requestsSent += 1;
      await sleep(250);
    }

    while (Date.now() < deadline) {
      const range = await getCachedWindowRange(
        accountKey,
        currency,
        fromTs,
        toTs,
      );
      if (range && rangeCoversRequest(range, fromTs, toTs)) {
        return {
          symbol: currency,
          requestedFrom: fromDate,
          requestedTo: toDate,
          loaded: true,
          hubConnected: true,
          requestsSent,
          ...buildMarketDataRangeSummary({
            requestedFrom: fromDate,
            requestedTo: toDate,
            barCount: range.count,
            minTimestampMs: range.minTs,
            maxTimestampMs: range.maxTs,
          }),
        };
      }
      await sleep(POLL_MS);
    }

    const range = await getCachedWindowRange(
      accountKey,
      currency,
      fromTs,
      toTs,
    );
    if (!range) {
      return {
        symbol: currency,
        requestedFrom: fromDate,
        requestedTo: toDate,
        loaded: false,
        hubConnected: true,
        requestsSent,
        barCount: 0,
        partialCache: true,
        cacheWarning:
          "No OHLC bars received in time — symbol must be on the watchlist; batches may still arrive for ~60s.",
      };
    }

    return {
      symbol: currency,
      requestedFrom: fromDate,
      requestedTo: toDate,
      loaded: false,
      hubConnected: true,
      requestsSent,
      ...buildMarketDataRangeSummary({
        requestedFrom: fromDate,
        requestedTo: toDate,
        barCount: range.count,
        minTimestampMs: range.minTs,
        maxTimestampMs: range.maxTs,
      }),
    };
  } finally {
    schedulePrefetchTargetUnregister(currency);
  }
}

export async function getOhlcCacheStatus(
  currency: string,
): Promise<Record<string, unknown>> {
  const accountKey = await resolveActiveAccountKey();
  await marketDataDb.invalidateSymbolCache(accountKey, currency);
  const bars = await loadIdbBarsForSymbol(accountKey, currency);
  const range = barsRangeFromSeries(bars);
  if (!range) {
    return { symbol: currency.toUpperCase(), cached: false, barCount: 0 };
  }
  const { dayKeyFromTimestampMs } = await import(
    "@/lib/terminal/engine/shared/market-data"
  );
  return {
    symbol: currency.toUpperCase(),
    cached: true,
    barCount: range.count,
    minTimestampMs: range.minTs,
    maxTimestampMs: range.maxTs,
    actualFromDate: dayKeyFromTimestampMs(range.minTs),
    actualToDate: dayKeyFromTimestampMs(range.maxTs),
  };
}
